import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionsCell,
  ActionButton,
  EmptyMessage,
  StyledTable as Table,
  StyledTableHead as THead,
  StyledTableRow as TR,
  StyledTableHeader as TH,
  StyledTableCell as TD,
  TableContainer,
  DialogOverlay,
  DialogPanel,
  DialogTitle,
  DialogText,
  DialogFooter,
  DialogButton,
  DesktopOnly,
  MobileCards,
  MobileCard,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardActions,
  PanelRoot,
  StyledButtonSpan,
} from "../AlertRequestsPanel/styles";
import {
  LoadBanner,
  LoadTitle,
  LoadText,
  MetricsGrid,
  MetricItem,
  MetricHeader,
  MetricBar,
  MetricDetail,
  SummaryGrid,
  SummaryCard,
  ReasonList,
  Toolbar,
  FiltersRow,
  FilterSelect,
  ClearFiltersButton,
  SearchInput,
  StatusBadge,
  MutedText,
  CheckboxRow,
  WarningNote,
  ReportButton,
  ButtonSpinner,
  ProjectAreaStack,
  ProjectAreaRow,
  FieldTag,
  FieldValue,
} from "./styles";
import { guardianAlertService } from "../../../services/guardianAlertService";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import {
  SERVER_LOAD_LEVEL,
  PAUSE_REASON_FILTERS,
  PAUSE_REASON_FILTER_LABELS,
  describeNotificationStatus,
  filterUnansweredAlerts,
  formatPercent,
  formatSendsProgress,
  hasAppRecipients,
  listUnansweredAreas,
  listUnansweredProjects,
} from "../../../utils/alertNotificationsReview";

const LOAD_TITLES = {
  [SERVER_LOAD_LEVEL.OK]: "Servidor com carga normal",
  [SERVER_LOAD_LEVEL.WARNING]: "Servidor próximo do limite de processamento",
  [SERVER_LOAD_LEVEL.CRITICAL]: "Servidor com processamento sobrecarregado",
};

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ServerLoadBanner({ load, message }) {
  if (!load) return null;
  const level = load.level || SERVER_LOAD_LEVEL.OK;
  const text =
    message ||
    (level === SERVER_LOAD_LEVEL.OK
      ? `Ocupação atual de ${formatPercent(load.usage_percent)}. O aviso aparece a partir de ${formatPercent(load.warning_percent)}.`
      : `O servidor está com ${formatPercent(load.usage_percent)} de ocupação (limite de alerta: ${formatPercent(load.warning_percent)}). Revise os alertas não respondidos: os envios deles consomem processamento e precisam ser respondidos ou permanecer desligados.`);

  return (
    <LoadBanner $level={level} role={level === SERVER_LOAD_LEVEL.OK ? "status" : "alert"}>
      <LoadTitle $level={level}>{LOAD_TITLES[level] || LOAD_TITLES.ok}</LoadTitle>
      <LoadText>{text}</LoadText>
      <MetricsGrid>
        {(load.metrics || []).map((metric) => (
          <MetricItem key={metric.key}>
            <MetricHeader>
              <span>{metric.label}</span>
              <span>{formatPercent(metric.percent)}</span>
            </MetricHeader>
            <MetricBar $percent={metric.percent} $level={metric.level} />
            {metric.detail ? <MetricDetail>{metric.detail}</MetricDetail> : null}
          </MetricItem>
        ))}
      </MetricsGrid>
    </LoadBanner>
  );
}

function RecipientsInfo({ row }) {
  const withApp = Number(row.recipients_with_app_count) || 0;
  const total = Number(row.recipients_count) || 0;
  return (
    <>
      {withApp}/{total} com app
      {total > 0 && withApp === 0 ? <MutedText>Push não é entregue</MutedText> : null}
    </>
  );
}

function ProjectAreaCell({ projectTitle, areaName }) {
  return (
    <ProjectAreaStack>
      <ProjectAreaRow>
        <FieldTag $kind="project">Projeto</FieldTag>
        <FieldValue $kind="project">{projectTitle || "Sem projeto"}</FieldValue>
      </ProjectAreaRow>
      <ProjectAreaRow>
        <FieldTag $kind="area">Área</FieldTag>
        <FieldValue $kind="area">{areaName || "Sem área"}</FieldValue>
      </ProjectAreaRow>
    </ProjectAreaStack>
  );
}

/**
 * ADM/ENG: alertas não respondidos, quais estão consumindo envios e quais
 * foram desligados, com reativação manual.
 */
export default function UnansweredAlertsPanel({ loginAttention = null }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useModalContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pauseReasonFilter, setPauseReasonFilter] = useState(
    PAUSE_REASON_FILTERS.ALL
  );
  const [projectFilter, setProjectFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [search, setSearch] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [resendEmail, setResendEmail] = useState(true);
  const [reactivatingId, setReactivatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await guardianAlertService.getUnansweredAlerts());
    } catch (error) {
      console.error("Erro ao carregar alertas não respondidos:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!confirmRow) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !reactivatingId) setConfirmRow(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmRow, reactivatingId]);

  const allResults = data?.results;

  const projectOptions = useMemo(
    () => listUnansweredProjects(allResults),
    [allResults]
  );

  const areaOptions = useMemo(
    () => listUnansweredAreas(allResults, { project: projectFilter }),
    [allResults, projectFilter]
  );

  useEffect(() => {
    if (!areaFilter) return;
    const stillValid = areaOptions.some(
      (name) => name.toLowerCase() === areaFilter.toLowerCase()
    );
    if (!stillValid) setAreaFilter("");
  }, [areaOptions, areaFilter]);

  const rows = useMemo(
    () =>
      filterUnansweredAlerts(allResults, {
        status: statusFilter,
        pauseReason: pauseReasonFilter,
        project: projectFilter,
        area: areaFilter,
        search,
      }),
    [
      allResults,
      statusFilter,
      pauseReasonFilter,
      projectFilter,
      areaFilter,
      search,
    ]
  );

  const hasExtraFilters =
    pauseReasonFilter !== PAUSE_REASON_FILTERS.ALL ||
    Boolean(projectFilter) ||
    Boolean(areaFilter) ||
    Boolean(search.trim());

  const clearExtraFilters = () => {
    setPauseReasonFilter(PAUSE_REASON_FILTERS.ALL);
    setProjectFilter("");
    setAreaFilter("");
    setSearch("");
  };

  const summary = data?.summary;
  const serverLoad = data?.server_load || loginAttention?.server_load || null;
  const pausedReasons = (summary?.paused_by_reason || []).filter((r) => r.count > 0);

  const openConfirm = (row) => {
    setResendEmail(true);
    setConfirmRow(row);
  };

  const confirmReactivate = async () => {
    if (!confirmRow) return;
    setReactivatingId(confirmRow.id);
    try {
      const updated = await guardianAlertService.reactivateAlertNotifications(
        confirmRow.id,
        { resendEmail }
      );
      setData((prev) =>
        prev
          ? {
              ...prev,
              results: prev.results.map((r) => (r.id === updated.id ? updated : r)),
            }
          : prev
      );
      setConfirmRow(null);
      showSuccess(
        "Envios reativados",
        resendEmail
          ? `Alerta ${updated.cod_alerta}: o push e o e-mail estão sendo enviados em segundo plano.`
          : `Alerta ${updated.cod_alerta}: o push está sendo enviado em segundo plano.`
      );
      load();
    } catch (error) {
      showError(
        "Não foi possível reativar",
        error?.response?.data?.detail || "Tente novamente em instantes."
      );
    } finally {
      setReactivatingId(null);
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const blob = await guardianAlertService.getUnansweredAlertsReportPDF();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toLocaleDateString("pt-BR").replace(/\//g, "-");
      link.download = `relatorio_alertas_nao_respondidos_${today}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess(
        "Relatório gerado",
        "O download do PDF foi iniciado. Ele traz os alertas não respondidos separados por projeto e área, agrupados por motivo."
      );
    } catch (error) {
      console.error("Erro ao gerar relatório de alertas não respondidos:", error);
      showError(
        "Não foi possível gerar o relatório",
        error?.response?.data?.detail || "Tente novamente em instantes."
      );
    } finally {
      setReportLoading(false);
    }
  };

  const openAlert = (row) => {
    if (row.cod_alerta) navigate(`/alerts/buscar/${row.cod_alerta}`);
  };

  const renderActions = (row) => (
    <>
      <ActionButton type="button" variant="create" onClick={() => openAlert(row)}>
        Ver alerta
      </ActionButton>
      {!row.notifications_active ? (
        <ActionButton
          type="button"
          variant="create"
          disabled={reactivatingId === row.id}
          onClick={() => openConfirm(row)}
        >
          Reativar envios
        </ActionButton>
      ) : null}
    </>
  );

  return (
    <PanelRoot>
      <ServerLoadBanner
        load={serverLoad}
        message={!data && loginAttention?.message ? loginAttention.message : ""}
      />

      {summary ? (
        <>
          <SummaryGrid>
            <SummaryCard
              type="button"
              $selected={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
            >
              <strong>{summary.unanswered}</strong>
              <span>Alertas não respondidos</span>
            </SummaryCard>
            <SummaryCard
              type="button"
              $selected={statusFilter === "active"}
              onClick={() => setStatusFilter("active")}
            >
              <strong>{summary.active}</strong>
              <span>Com envios ativos (consumindo o servidor)</span>
            </SummaryCard>
            <SummaryCard
              type="button"
              $selected={statusFilter === "paused"}
              onClick={() => setStatusFilter("paused")}
            >
              <strong>{summary.paused}</strong>
              <span>Com envios desligados</span>
            </SummaryCard>
          </SummaryGrid>
          {pausedReasons.length ? (
            <ReasonList aria-label="Motivos de desligamento">
              {pausedReasons.map((r) => (
                <li key={r.reason}>
                  {r.label}: <strong>{r.count}</strong>
                </li>
              ))}
            </ReasonList>
          ) : null}
        </>
      ) : null}

      <Toolbar>
        <SearchInput
          type="search"
          placeholder="Buscar por código, projeto, área ou severidade"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Buscar alertas não respondidos"
        />
        <ActionButton type="button" variant="create" onClick={load} disabled={loading}>
          {loading ? "Atualizando…" : "Atualizar"}
        </ActionButton>
      </Toolbar>

      <FiltersRow>
        <FilterSelect
          value={pauseReasonFilter}
          onChange={(e) => setPauseReasonFilter(e.target.value)}
          aria-label="Filtrar por motivo do desligamento"
        >
          {Object.entries(PAUSE_REASON_FILTER_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          value={projectFilter}
          onChange={(e) => {
            setProjectFilter(e.target.value);
            setAreaFilter("");
          }}
          aria-label="Filtrar por projeto"
        >
          <option value="">Todos os projetos</option>
          {projectOptions.map((title) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          aria-label="Filtrar por área"
        >
          <option value="">
            {projectFilter ? "Todas as áreas do projeto" : "Todas as áreas"}
          </option>
          {areaOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </FilterSelect>

        {hasExtraFilters ? (
          <ClearFiltersButton type="button" onClick={clearExtraFilters}>
            Limpar filtros
          </ClearFiltersButton>
        ) : null}

        <ReportButton
          type="button"
          onClick={handleGenerateReport}
          disabled={reportLoading}
          aria-busy={reportLoading}
        >
          {reportLoading ? (
            <>
              <ButtonSpinner aria-hidden="true" />
              Gerando relatório... Por favor, aguarde
            </>
          ) : (
            "📄 Gerar relatório"
          )}
        </ReportButton>
      </FiltersRow>

      {loading && !data ? (
        <EmptyMessage>Carregando alertas não respondidos...</EmptyMessage>
      ) : !data ? (
        <EmptyMessage>Não foi possível carregar os alertas não respondidos.</EmptyMessage>
      ) : rows.length === 0 ? (
        <EmptyMessage>Nenhum alerta não respondido neste filtro.</EmptyMessage>
      ) : (
        <TableContainer>
          <DesktopOnly>
            <Table>
              <THead>
                <TR>
                  <TH>Código</TH>
                  <TH>Projeto / Área</TH>
                  <TH>Severidade</TH>
                  <TH>Criado em</TH>
                  <TH>Envios</TH>
                  <TH>Destinatários</TH>
                  <TH>Status dos envios</TH>
                  <TH>Próximo envio</TH>
                  <TH>Ações</TH>
                </TR>
              </THead>
              <tbody>
                {rows.map((row) => {
                  const st = describeNotificationStatus(row);
                  return (
                    <TR key={row.id}>
                      <TD>
                        <StyledButtonSpan onClick={() => openAlert(row)}>
                          {row.cod_alerta}
                        </StyledButtonSpan>
                      </TD>
                      <TD>
                        <ProjectAreaCell
                          projectTitle={row.project_title}
                          areaName={row.area_name}
                        />
                      </TD>
                      <TD>
                        {row.severity || "—"}
                        <MutedText>{row.alert_type}</MutedText>
                      </TD>
                      <TD>{formatDate(row.created_at)}</TD>
                      <TD>{formatSendsProgress(row)}</TD>
                      <TD>
                        <RecipientsInfo row={row} />
                      </TD>
                      <TD>
                        <StatusBadge $tone={st.tone}>{st.label}</StatusBadge>
                        {row.notifications_reactivated_by ? (
                          <MutedText>
                            Reativado por {row.notifications_reactivated_by} em{" "}
                            {formatDate(row.notifications_reactivated_at)}
                          </MutedText>
                        ) : null}
                      </TD>
                      <TD>
                        {row.notifications_active
                          ? formatDate(row.next_notification_at)
                          : "—"}
                      </TD>
                      <TD>
                        <ActionsCell>{renderActions(row)}</ActionsCell>
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          </DesktopOnly>

          <MobileCards>
            {rows.map((row) => {
              const st = describeNotificationStatus(row);
              return (
                <MobileCard key={`m-${row.id}`}>
                  <MobileCardTitle>
                    <StyledButtonSpan onClick={() => openAlert(row)}>
                      {row.cod_alerta}
                    </StyledButtonSpan>
                  </MobileCardTitle>
                  <MobileCardMeta as="div">
                    <ProjectAreaCell
                      projectTitle={row.project_title}
                      areaName={row.area_name}
                    />
                  </MobileCardMeta>
                  <MobileCardMeta>
                    {row.severity || "—"} · {formatDate(row.created_at)}
                  </MobileCardMeta>
                  <MobileCardMeta>
                    Envios: {formatSendsProgress(row)} ·{" "}
                    <RecipientsInfo row={row} />
                  </MobileCardMeta>
                  <div>
                    <StatusBadge $tone={st.tone}>{st.label}</StatusBadge>
                  </div>
                  <MobileCardActions>{renderActions(row)}</MobileCardActions>
                </MobileCard>
              );
            })}
          </MobileCards>
        </TableContainer>
      )}

      {confirmRow ? (
        <DialogOverlay>
          <DialogPanel
            role="dialog"
            aria-modal="true"
            aria-labelledby="reactivate-alert-title"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogTitle id="reactivate-alert-title">Reativar envios</DialogTitle>
            <DialogText>
              O alerta <strong>{confirmRow.cod_alerta}</strong> volta a receber
              lembretes até o limite de {confirmRow.max_notifications} envio(s)
              da severidade {confirmRow.severity || "—"}. O primeiro sai agora.
            </DialogText>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={resendEmail}
                onChange={(e) => setResendEmail(e.target.checked)}
              />
              Reenviar também o e-mail do alerta aos destinatários
            </CheckboxRow>
            {!hasAppRecipients(confirmRow) ? (
              <WarningNote>
                Nenhum destinatário desta área tem o app cadastrado: o push não
                será entregue e os envios voltarão a ser desligados. Cadastre o
                app para os responsáveis antes de reativar
                {resendEmail ? "; o e-mail será reenviado normalmente." : "."}
              </WarningNote>
            ) : null}
            <DialogFooter>
              <DialogButton
                type="button"
                $variant="ghost"
                disabled={reactivatingId === confirmRow.id}
                onClick={() => setConfirmRow(null)}
              >
                Cancelar
              </DialogButton>
              <DialogButton
                type="button"
                $variant="primary"
                disabled={reactivatingId === confirmRow.id}
                onClick={confirmReactivate}
              >
                {reactivatingId === confirmRow.id ? "Reativando…" : "Reativar"}
              </DialogButton>
            </DialogFooter>
          </DialogPanel>
        </DialogOverlay>
      ) : null}
    </PanelRoot>
  );
}
